import React, { useState, useEffect } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import { Form, Button, Row, Col } from "react-bootstrap";
import { updateDb } from "../services/updateDb";
import useAxiosFetch from "../common/useAxiosFetch";
import AlertMessage from "../common/AlertMessage";
import { useAppContext } from "../AppContext";
import authHeader from "../services/auth-header";
import { API_BASE_URL } from "../common/config";
import { MODAL_TYPES } from "../modals/modalTypes";

import moment from "moment";
import { useTranslation } from 'react-i18next';
import { Options } from "../risks/iso-verklaringen";
import { getCategoryValue, expandCategories, getRiskScoreClass } from "../common/helpers";
import useSoaCheck from "../common/useSoaCheck";

const RiskForm = ({formRef, measures, setMeasures, riskData, riskId, handleClose, setRefreshData, setTempRiskState, isRiskTemplate, finalRiskTemplatePage, submitAllRisks, isFormModified, setIsFormModified, previousOwners = []}) => {
    const { t, i18n } = useTranslation();
    const { currentOrg, currentProject, alertMessage, showAlertMessage, openModal } = useAppContext();
    const { data } = useAxiosFetch("/api/risks/measures/" + currentOrg.id); 
    const { checkSoaAfterMeasureUpdate } = useSoaCheck();
    const [measureOptions, setMeasureOptions] = useState([{ measure: '', category: [] }]);
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [aiSuggesting, setAiSuggesting] = useState(false);
    const [aiSuggested, setAiSuggested] = useState(false);

    const defaultRiskState = {
        date: moment().format("YYYY-MM-DD"),
        projectId: currentProject.id,
        owner: "",
        description: "",
        availability: false,
        integrity: false,
        confidentiality: false,
        'initial-chance': 0,
        'initial-impact': 0,
        'expected-chance': 0,
        'expected-impact': 0,
        treatment: "reduce",
        treatmentHow: "",
        tempAcceptTreatment: false,
        acceptTreatmentDate: ""
    };

    const [riskState, setRiskState] = useState(defaultRiskState);

    useEffect(() => {
        if (riskData) {
            setRiskState({
                ...defaultRiskState, 
                ...Object.keys(defaultRiskState).reduce((acc, key) => {
                    if (key in riskData) {
                        acc[key] = riskData[key]; // Only overwrite if key exists in riskData
                    }                    
                    return acc;
                }, {})
            });

            setTempRiskState(() => ({
                ...defaultRiskState,
                ...riskData
            }));
        }
    }, [riskData]);

    useEffect(() => {
        if (data) {
            setMeasureOptions(data['measures'].map(measure => ({
                measure: measure.measure,
                category: expandCategories(measure.category, i18n.language, currentOrg.securityStandard)
            })));
            // Combine unique owners from API with previous owners from batch import
            const combinedOwners = [...new Set([...previousOwners, ...data['uniqueOwners']])];
            setOwnerOptions(combinedOwners);
        }
    }, [data, previousOwners]);


    const handleInputChange = (e) => {
        setIsFormModified(true);
        const { name, value, checked, type } = e.target;
        
        let updatedValue;
        if (type === "checkbox") {
            updatedValue = checked;
        } else if (type === "select-one" && (name === 'initial-chance' || name === 'initial-impact' || name === 'expected-chance' || name === 'expected-impact')) {
            updatedValue = parseInt(value, 10); // Convert select values to integers
        } else {
            updatedValue = value;
        }

        setRiskState(prevState => ({
            ...prevState,
            [name]: updatedValue
        }));
        
        setTempRiskState(prevState => ({
            ...prevState,
            [name]: updatedValue
        }));
    };

    const submitForm = async (e) => {
        e.preventDefault();
    
        let riskObj = riskState;
            
        if (parseInt(riskObj["initial-chance"]*riskObj["initial-impact"]) < parseInt(riskObj["expected-chance"]*riskObj["expected-impact"])) {
            showAlertMessage('error', t('initial_score_must_be_higher_than_expected_score'));
            return false;
        }
    
        if (!riskObj.availability && !riskObj.integrity && !riskObj.confidentiality) {
            showAlertMessage('error', t('at_least_one_cia'));
            return false;
        }

        riskObj.measures = measures;
        
        // only set update-reasons and projectId when creating
        // Collect affected categories from measures for SoA check
        const affectedCategories = [...new Set(
            measures.flatMap(m => (m.category || []).map(c => getCategoryValue(c)))
        )];

        if(riskData) {
            const res = await updateDb("/api/risks/update/" + riskId, riskObj);
    
            if (res.success) {
                setIsFormModified(false);
                handleClose(true);

                setRefreshData(new Date().getTime());
                showAlertMessage('warning', t('risk') + " " + t('updated'));

                // Check if any SoA controls should be marked as implemented
                if (affectedCategories.length > 0) {
                    checkSoaAfterMeasureUpdate(affectedCategories);
                }
            }
            else {
                showAlertMessage('error', t('error_occured') + res.error);
            }
        }
        else {
            const res = await updateDb("/api/risks/", riskObj);
    
            if (res.success) {
                setIsFormModified(false);
                handleClose(true);

                setRefreshData(new Date().getTime());
                showAlertMessage('success', t('risk') + " " + t('added'));

                // Check if any SoA controls should be marked as implemented
                if (affectedCategories.length > 0) {
                    checkSoaAfterMeasureUpdate(affectedCategories);
                }
            }
            else {
                showAlertMessage('error', t('error_occured') + res.error);
            }
        }
    };

    const getScoreColorClass = (score) => getRiskScoreClass(score, currentOrg.rangeBounds);

    let handleMeasureChange = (i, e) => {
        setIsFormModified(true);
        
        let newMeasures = [...measures];

        if (!e.target)
            newMeasures[i]["category"] = e;
        else if (e.target.type === "text" || e.target.type === "textarea") // handle both input and textarea
            newMeasures[i]["measure"] = e.target.value;
        else if (e.target.type === "select-one")
            newMeasures[i]["status"] = e.target.value;

        setMeasures(newMeasures);
    };

    let handleMeasureTypeahead = (i, e) => {
        // if typeof e is array with length 0 then return 
        // to avoid setting empty measure
        if (Array.isArray(e) && e.length === 0) {
            return;
        }
       
        setIsFormModified(true);

        let newMeasures = [...measures];
        newMeasures[i]["measure"] = e['measure'] ? e['measure'] : e;

        if( e['category']) //  don't overwrite if category is not found /set
            newMeasures[i]["category"] = e['category'];

        setMeasures(newMeasures);
    };

    const handleOwnerTypeahead = (e) => {
        // if typeof e is array with length 0 then return 
        if (Array.isArray(e) && e.length === 0) {
            return;
        }

        // if e is a string that's the owner, otherwise if e[0].owner exists thats the owner, otherwise e[0] is the owner
        let value = typeof e === 'string' ? e : e[0].owner ? e[0].owner : e[0];

        // trigger handleInputChange with the owner value
        handleInputChange({ target: { name: "owner", value } });
    };


    let addMeasureFields = () => {
        setMeasures([...measures, { measure: "", category: [] }]);
    };

    let removeMeasureFields = (i) => {
        let newMeasures = [...measures];
        newMeasures.splice(i, 1);
        setMeasures(newMeasures);
    };

    const isMeasureEmpty = (m) => !m || (!m.measure?.trim() && (!m.category || m.category.length === 0));

    const suggestMeasuresWithAI = async () => {
        if (aiSuggesting || aiSuggested || !riskState.description?.trim()) {
            return;
        }

        setAiSuggesting(true);

        try {
            const reqHeaders = authHeader();
            reqHeaders["Content-Type"] = "application/json";

            const existingMeasureTexts = measures
                .filter(m => !isMeasureEmpty(m))
                .map(m => (m.measure || '').trim())
                .filter(Boolean);

            const response = await fetch(`${API_BASE_URL}/api/risks/suggest-measures`, {
                method: "post",
                headers: reqHeaders,
                body: JSON.stringify({
                    description: riskState.description,
                    confidentiality: riskState.confidentiality,
                    integrity: riskState.integrity,
                    availability: riskState.availability,
                    language: i18n.language === 'nl' ? 'nl' : 'en',
                    existingMeasures: existingMeasureTexts,
                }),
            });

            // AI usage limit reached -- prompt to upgrade (same UX as the measures CoPilot)
            if (response.status === 403) {
                const errorData = await response.json();
                if (errorData.upgradeRequired) {
                    openModal(MODAL_TYPES.UPGRADE, {
                        featureType: 'AI',
                        currentLimit: errorData.limit,
                        currentCount: errorData.current,
                        recommendedPlan: 'premium',
                    });
                    return;
                }
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                showAlertMessage('error', t('error_occured') + (errorData.msg || ''));
                return;
            }

            const { measures: suggested } = await response.json();

            if (!suggested || suggested.length === 0) {
                showAlertMessage('warning', t('no_ai_suggestions'));
                return;
            }

            // Keep any measures the user already entered, drop empty placeholder rows,
            // then append only the AI suggestions that aren't duplicates of existing ones.
            const existing = measures.filter(m => !isMeasureEmpty(m));
            const existingKeys = new Set(
                existing.map(m => (m.measure || '').toLowerCase().replace(/\s+/g, ' ').trim())
            );
            const newSuggestions = suggested.filter(
                s => !existingKeys.has((s.measure || '').toLowerCase().replace(/\s+/g, ' ').trim())
            );

            if (newSuggestions.length === 0) {
                showAlertMessage('warning', t('no_ai_suggestions'));
                return;
            }

            setMeasures([...existing, ...newSuggestions]);
            setIsFormModified(true);
            setAiSuggested(true);
            showAlertMessage('success', t('ai_suggestions_added'));
        }
        catch (error) {
            showAlertMessage('error', t('error_occured') + (error?.message || ''));
        }
        finally {
            setAiSuggesting(false);
        }
    };

    return (
    <Form ref={formRef} onSubmit={submitForm} id="addRisk">
        <AlertMessage type={alertMessage.type} message={alertMessage.content} />

        <fieldset className="fieldset-form mb-3">
                
            <legend className="float-none w-auto">{t('risk')}</legend>

            <Row className="mb-3">
                <Form.Group as={Col}>
                    <Form.Label><span className="blue-bg">{t('date')}</span></Form.Label>
                    <Form.Control type="date" name="date" value={riskState.date} onChange={handleInputChange} required />
                </Form.Group>

                <Form.Group as={Col} >
                    <Form.Label><span className="blue-bg">{t('risk_owner')}</span></Form.Label>

                    <Typeahead
                        id={"basic-typeahead-owner-single"}
                        className="input-owner-typeahead"
                        labelKey="owner"
                        onChange={handleOwnerTypeahead}
                        onInputChange={handleOwnerTypeahead}
                        options={ownerOptions}
                        placeholder={" " + t('risk_owner_placeholder')}
                        allowNew
                        newSelectionPrefix={t('new_owner') + ": "}
                        selected={riskState.owner ? [ riskState.owner ] : []}
                        name="owner"
                        inputProps={{ required: true }}
                    /> 

                </Form.Group>
            </Row>

            <Row className="mb-3 align-items-center">

                <Form.Group as={Col} lg={9}>
                    <Form.Label><span className="blue-bg">{t('description')}</span></Form.Label>
                    <Form.Control as="textarea" rows={3} name="description" placeholder={t('description_placeholder')} value={riskState.description} onChange={handleInputChange} required />
                    {riskState.description?.trim() && !aiSuggested && (
                        <div className="ai-suggest-hint" onClick={suggestMeasuresWithAI}>
                            <i className="icon-stars1" /> {t('ai_suggest_hint')}
                        </div>
                    )}
                </Form.Group>

                <Form.Group as={Col}>

                    <Form.Label><span className="blue-bg">{t('cia').toUpperCase()}</span></Form.Label>

                    <Form.Check type="checkbox" name="confidentiality" label={t('confidentiality')} onChange={handleInputChange} checked={riskState.confidentiality} />
                    <Form.Check type="checkbox" name="integrity" label={t('integrity')} onChange={handleInputChange} checked={riskState.integrity} />
                    <Form.Check type="checkbox" name="availability" label={t('availability')} onChange={handleInputChange} checked={riskState.availability} />
                </Form.Group>
            </Row>

            <Row>
                <Col><span className="blue-bg">{t('initial')} {t('risk').toLowerCase()}</span></Col>
                <Col><span className="blue-bg">{t('target')} {t('risk').toLowerCase()}</span></Col>
            </Row>


            <Row className="mb-3">
                <Form.Group as={Col} controlId="formInitialChance">
                <Form.Label>{t('chance')}</Form.Label>
                <Form.Select name="initial-chance" value={riskState['initial-chance']} onChange={handleInputChange}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <option key={i} value={i}>{i === 0 ? '-' : i}</option>
                    ))}
                </Form.Select>
                </Form.Group>

                <Form.Group as={Col} controlId="formInitialImpact">
                <Form.Label>{t('impact')}</Form.Label>
                <Form.Select name="initial-impact" value={riskState['initial-impact']} onChange={handleInputChange}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <option key={i} value={i}>{i === 0 ? '-' : i}</option>
                    ))}
                </Form.Select>
                </Form.Group>

                <Form.Group as={Col} controlId="formInitialScore">
                <Form.Label>Score</Form.Label>
                <Form.Control
                    name="initial-score"
                    value={riskState['initial-impact']*riskState['initial-chance'] === 0 ? '-' : riskState['initial-impact']*riskState['initial-chance']}
                    className={getScoreColorClass(riskState['initial-impact']*riskState['initial-chance'])}
                    readOnly
                    data-readonly
                />
                </Form.Group>

                <Form.Group as={Col} controlId="formExpectedChance">
                <Form.Label>{t('chance')}</Form.Label>
                <Form.Select name="expected-chance" value={riskState['expected-chance']} onChange={handleInputChange}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <option key={i} value={i}>{i === 0 ? '-' : i}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            <Form.Group as={Col} controlId="formExpectedImpact">
            <Form.Label>{t('impact')}</Form.Label>
            <Form.Select name="expected-impact" value={riskState['expected-impact']} onChange={handleInputChange}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <option key={i} value={i}>{i === 0 ? '-' : i}</option>
                ))}
            </Form.Select>
            </Form.Group>

            <Form.Group as={Col} controlId="formExpectedScore">
            <Form.Label>Score</Form.Label>
            <Form.Control
                name="expected-score"
                value={riskState['expected-impact']*riskState['expected-chance'] === 0 ? '-' : riskState['expected-impact']*riskState['expected-chance']}
                className={getScoreColorClass(riskState['expected-impact']*riskState['expected-chance'])}
                readOnly
                data-readonly
            />
            </Form.Group>

            </Row>

            <Row>
                <Form.Group as={Col} controlId="treatment">
                    <Form.Label><span className="blue-bg">{t('risk_treatment')}</span></Form.Label>
                    <Form.Select name="treatment" value={riskState.treatment} onChange={handleInputChange}>
                        <option value="reduce">{t('reduce')}</option>
                        <option value="accept">{t('accept')}</option>
                        <option value="avoid">{t('avoid')}</option>
                        <option value="transfer">{t('transfer')}</option>
                    </Form.Select>
                </Form.Group>

                {(riskState.treatment === "avoid" || riskState.treatment =="transfer") && (
                    <Form.Group as={Col} controlId="treatmentHow">
                        <Form.Label><span className="blue-bg">{t('how')}?</span></Form.Label>
                        <Form.Control
                            as="input"
                            name="treatmentHow"
                            value={riskState.treatmentHow}
                            onChange={handleInputChange}
                            placeholder={riskState.treatment === "avoid" ? t('how_avoid') : t('how_transfer')}
                        />
                    </Form.Group>
                )}

                {(riskState.treatment === "accept") && (
                    <Form.Group as={Col} controlId="tempAcceptTreatment">
                        <Form.Label>&nbsp;</Form.Label>
                        <div onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                                type="checkbox"
                                id="tempAcceptTreatment"
                                name="tempAcceptTreatment"
                                label={t('temporarily_accept') + "?"}
                                onChange={handleInputChange}
                                checked={riskState.tempAcceptTreatment}
                                style={{ width: 'fit-content' }}
                            />
                        </div>
                    </Form.Group>
                )}

                {(riskState.treatment === "accept" && riskState.tempAcceptTreatment) && (
                    <Form.Group as={Col} controlId="acceptTreatmentDate">
                        <Form.Label><span className="blue-bg">{t('until_when')}</span></Form.Label>
                        <Form.Control
                            type="date"
                            name="acceptTreatmentDate"
                            value={riskState.acceptTreatmentDate}
                            onChange={handleInputChange}
                            placeholder={t('until_when_placeholder')}
                        />
                    </Form.Group>
                )}

            </Row>            
            <Row/>&nbsp;
        </fieldset>

        <fieldset className="fieldset-form mb-3">
        <Button className="add-btn" variant="primary" type="button" onClick={addMeasureFields}>
            +
        </Button>

        {riskState.description?.trim() && !aiSuggested && (
            <Button
                className="add-btn ai-suggest-btn"
                variant="primary"
                type="button"
                onClick={suggestMeasuresWithAI}
                disabled={aiSuggesting}
                title={t('ai_suggest_measures')}
            >
                <i className={aiSuggesting ? "icon-spinner fa-spin" : "icon-stars1"} aria-hidden="true" />
            </Button>
        )}

        <legend className="float-none w-auto">{t('measures')}</legend>
        {measures.map((el, index) => (
            <Row className="mb-3" key={index}>
            <Form.Group as={Col} controlId={"measure-" + index}>
                {riskData && !isRiskTemplate ? (
                    <Form.Control
                        as="textarea"
                        rows={el.measure && el.measure.length > 80 ? 3 : el.measure && el.measure.length > 40 ? 2 : 1}
                        placeholder={t('measure')}
                        value={el.measure}
                        onChange={(e) => handleMeasureChange(index, e)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && index === measures.length - 1) {
                                addMeasureFields();
                                let newMeasureIndex = measures.length;
                                // use a timeout to give React time to render the new input before trying to focus
                                setTimeout(() => {
                                    document.querySelector(`#measure-${newMeasureIndex}`).focus();
                                }, 0);
                            }
                        }}
                    />
                ) : (
                    <Typeahead
                        id={"basic-typeahead-single" + index}
                        labelKey="measure"
                        onChange={(e) => handleMeasureTypeahead(index, e && e[0] && e[0].measure ? e[0] : [])}
                        onInputChange={(e) => handleMeasureTypeahead(index, e)}
                        options={measureOptions}
                        placeholder={t('measure')}
                        allowNew
                        newSelectionPrefix={t('new_measure') + ": "}
                        selected={el.measure ? [{ measure: el.measure, category: el.category }] : []}
                        name="measure"
                        renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
                            <textarea
                                {...inputProps}
                                ref={(input) => {
                                    inputRef(input);
                                    referenceElementRef(input);
                                }}
                                rows={el.measure && el.measure.length > 80 ? 3 : el.measure && el.measure.length > 40 ? 2 : 1}
                                className="form-control"
                            />
                        )}
                    /> 
                )}

                {el.aiSuggested && (
                    <span className="ai-measure-badge" title={t('ai_suggested_measure')}>
                        <i className="icon-stars1" /> {t('ai_suggested')}
                    </span>
                )}

            </Form.Group>

            <Form.Group as={Col} controlId={"measurecat-" + index}>
                <Typeahead
                id={"basic-typeahead-multiple-" + index}
                labelKey="label"
                multiple
                onChange={(e) => handleMeasureChange(index, e)}
                options={Options(i18n.language, currentOrg.securityStandard).filter(
                    opt => !(measures[index].category || []).some(sel => getCategoryValue(sel) === opt.value)
                )}
                placeholder={t('category_placeholder')}
                selected={measures[index].category}
                name="category"
                />
            </Form.Group>

            <Form.Group as={Col} xs="auto" controlId={"measurestatus-" + index}>

                <Form.Select
                    value={el.status ? el.status : ""}
                    onChange={(e) => handleMeasureChange(index, e)}
                    onKeyDown={(e) => {
                        if (e.key === "Tab" && index === measures.length - 1) {
                            addMeasureFields();
                            let newMeasureIndex = measures.length;
                            // use a timeout to give React time to render the new input before trying to focus
                            setTimeout(() => {
                                document.querySelector(`#measure-${newMeasureIndex}`).focus();
                            }, 0);
                        }
                    }}
                >
                        <option value="">{t('measure')} status</option>
                        <option value="todo">{t('todo')}</option>
                        <option value="in_progress">{t('in_progress')}</option>
                        <option value="on_hold">{t('on_hold')}</option>
                        <option value="implemented">{t('implemented')}</option>
                        <option value="dismissed">{t('dismissed')}</option>
                </Form.Select>

            </Form.Group>

            <Form.Group as={Col} xs="auto">
                <i className="icon-delete"  style={{lineHeight: '38px'}} onClick={() => removeMeasureFields(index)} />
            </Form.Group>
            </Row>
        ))}
        </fieldset>

        <div className="white-border-btn cancel-btn" onClick={() => handleClose(false)}>
            {t('cancel')}
        </div>


        {!isRiskTemplate && (
            <input className="white-border-btn submit-btn" type="submit" value={riskData ? t('update_risk') : t('add_risk')} />
        )}

        {isRiskTemplate && finalRiskTemplatePage && (
            <div className="white-border-btn submit-btn" onClick={submitAllRisks}>{t('import_all_risks')}</div>
        )}

    </Form>
);
};
export default RiskForm;
